import { config } from "@/config";
import { getTokensSync, authenticate } from "@/features/auth/oktaClient";
import { getImpersonatedUserId } from "@/features/auth/impersonation";

/** Attaches Okta bearer/id tokens to backend requests; triggers re-auth if tokens are missing. */
export async function buildRequestInit(
  init?: RequestInit,
): Promise<RequestInit> {
  const headers: Record<string, string> = {};

  // Default to JSON for string bodies. FastAPI only parses the body as JSON when the
  // Content-Type is `application/json`; without it, `fetch` sends `text/plain` and the
  // raw string reaches Pydantic (→ 422 "Input should be a valid dictionary or object").
  // FormData is left untouched so the browser sets the multipart boundary itself.
  if (init?.body !== undefined && !(init.body instanceof FormData)) {
    headers["Content-Type"] = "application/json";
  }

  if (config.enableOktaAuth) {
    const isCallbackPath =
      window.location.pathname.startsWith("/login/callback");
    let tokens = isCallbackPath ? undefined : getTokensSync();
    if (!tokens && !isCallbackPath) {
      await authenticate();
      tokens = getTokensSync();
    }
    if (!tokens?.accessToken) {
      throw new Error("Not authenticated");
    }
    headers.Authorization = `Bearer ${tokens.accessToken}`;
    if (tokens.idToken) {
      headers["X-Id-Token"] = tokens.idToken;
    }
  }

  const impersonatedUserId = getImpersonatedUserId();
  if (impersonatedUserId) {
    headers["X-Impersonate-User-Id"] = impersonatedUserId;
  }

  return {
    ...init,
    headers: { ...headers, ...(init?.headers ?? {}) },
  };
}

/** Fetch wrapper prefixing the API base and attaching auth headers (parity with legacy httpClient). */
export async function httpClient(
  path: string,
  init?: RequestInit,
): Promise<Response> {
  const url = path.startsWith("http") ? path : `${config.apiBase}${path}`;
  return fetch(url, await buildRequestInit(init));
}
